export enum QUOTE_DATE_RANGE {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  THIS_WEEK = 'this_week',
  THIS_MONTH = 'this_month',
  LAST_MONTH = 'last_month',
  THIS_QUARTER = 'this_quarter',
  THIS_YEAR = 'this_year',
  LAST_QUARTER = 'last_quarter',
  LAST_YEAR = 'last_year',
}

export type QuoteSortField =
  | 'title'
  | 'quote_status'
  | 'total_costs'
  | 'profit'
  | 'quote_from'
  | 'created_at';

export enum QUOTE_STATUS {
  AWAITING_ACCEPTANCE = 'Awaiting Acceptance',
  ACCEPTED = 'Accepted',
  DECLINED = 'Declined',
}

export enum QUOTE_PROGRESS {
  PENDING = 'Pending',
  ACTIVE = 'Active',
}

export type AcceptanceDetails = {
  by: {
    email: string;
    phone: {
      type: string;
      value: string;
    };
    address: {
      zip: string;
      city: string;
      type: string;
      state: string;
      street: string;
      country: string;
    };
    name_last: string;
    name_first: string;
    company_name: string;
  };
  when: string;
  comments: string;
  order_number: string;
  accepted_on_behalf: boolean;
  accepted_on_behalf_who: string;
};

export type SelectedItem = {
  heading: string;
  discount: number;
  quantity: number;
  tax_rate: number;
  item_code: string;
  cost_price: number;
  item_total: number;
  unit_price: number;
  description: string;
  subscription: string;
  sales_category: string;
  tax_description: string;
};

export type Quote = {
  id: string;
  created_at: Date;
  updated_at: Date;
  first_sent: Date;
  quote_status: QUOTE_STATUS;
  quote_from: string;
  quote_number: number;
  title: string;
  quote_for_first_name: string;
  quote_for_last_name: string;
  total_includes_tax: number;
  total_excludes_tax: number;
  total_costs: number;
  profit: number;
  acceptance_details: AcceptanceDetails;
  currency: string;
  progress: QUOTE_PROGRESS;
  is_viewed: boolean;
  is_declined: false;
  decline_info: {
    [key: string]: string;
  };
  questions: Array<string>;
  valid_until: Date;
  overall_discount: number;
  discount_amount_excludes_tax: number;
  discount_amount_includes_tax: number;
  quote_file: {
    url: string;
  };
  views_list: Array<{
    by: {
      email: string;
      phone: {
        type: string;
        value: string;
      };
      address: {
        zip: string;
        city: string;
        type: string;
        state: string;
        street: string;
        country: string;
      };
      name_last: string;
      name_first: string;
      company_name: string;
    };
    when: string;
    total_views: string;
  }>;
  selected_items: SelectedItem[];
};

export type QuotesListStats = {
  total_quotes: number;
  total_value_ex_tax: number;
  total_profit: number;
  counts_by_status: {
    'Awaiting Acceptance': number;
    Accepted: number;
  };
  values_by_status: {
    'Awaiting Acceptance': number;
    Accepted: number;
  };
  profit_by_status: {
    'Awaiting Acceptance': number;
    Accepted: number;
  };
  counts_by_quote_from: {
    'Andy Newell': number;
    'Abby Walker': number;
  };
  values_by_quote_from: {
    'Andy Newell': number;
    'Abby Walker': number;
  };
};

export type QuoteListPerformance = {
  period: string;
  year: number;
  month: number;
  month_name: string;
  stats: {
    sent_count: number;
    won_count: number;
    sent_value: number;
    won_value: number;
    won_profit: number;
    avg_margin_percent: number;
    conversion_rate: number;
  };
  sales_reps: [
    {
      name: string;
      sent_count: number;
      won_count: number;
      sent_value: number;
      won_value: number;
      won_profit: number;
      conversion_rate: number;
    },
  ];
};

export interface QuotesList {
  itemsReceived: number;
  curPage: number;
  nextPage: number | null;
  prevPage: number | null;
  offset: number;
  perPage: number;
  itemsTotal: number;
  pageTotal: number;
  items: Quote[];
  stats?: QuotesListStats;
  performance?: QuoteListPerformance[];
  search_dates?: {
    startAtTo: string;
    startAtFrom: string;
  };
}

export interface SalesOpportunity {
  quote_ref?: string;
  created_date?: string;
  Customer?: string;
  Customer_id?: number;
  title?: string;
  value_inc_vat: number;
  costs?: number;
  profit?: number;
  lead_source?: string;
  department?: string;
  outcome?: string;
  issued_by?: string;
  contact_notes?: {
    NoteId: string;
    NoteType: string;
    Note: string;
  }[];
  [key: string]: unknown; // Allow for dynamic fields
}

export interface SalesTotals {
  number_of_deals: number;
  total_value_of_deals: number;
  total_cost_of_deals: number;
  total_profit_of_deals: number;
  [key: string]: unknown; // Allow for dynamic fields
}

export interface SalesData {
  related_sales_opportunities: SalesOpportunity[];
  review_of_sales_data?: string;
  totals: SalesTotals;
  [key: string]: unknown; // Allow for dynamic fields
}
